import set from 'lodash/set';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { updateComponent } from '~/app/components/dynamic-grid/dynamic-grid-helpers';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';
import { BulletListItem, BulletListStyle, ElementType } from './types';

type UseDynamicBulletListProps = {
  fieldPath?: string;
};

export const useDynamicBulletList = ({
  fieldPath = '',
}: UseDynamicBulletListProps = {}) => {
  const dispatch = useDispatch();
  const { flatOutline, selected: selectedComponentId } = useBuilder();
  const { componentPath, marketplaceOffers } = useDynamicGrid({
    componentId: selectedComponentId,
  });
  const currentComponent = flatOutline?.[selectedComponentId];

  const [items, setItems] = useState<BulletListItem[]>([]);
  const [elementType, setElementType] = useState<ElementType>('ul');
  const [listStyleType, setListStyleType] = useState<BulletListStyle>('disc');
  const [imageUrl, setImageUrl] = useState('');
  const [imageHeight, setImageHeight] = useState(30);
  const [imageWidth, setImageWidth] = useState(30);
  const [marketplaceOfferId, setMarketplaceOfferId] = useState('');
  const [rateAnnotation, setRateAnnotation] = useState('');
  const [listGap, setListGap] = useState(0);
  const [fontProperties, setFontProperties] = useState<{
    fontSize?: number;
    tablet?: {
      fontSize?: number;
    };
    mobile?: {
      fontSize?: number;
    };
  }>({
    fontSize: undefined,
  });

  const handleUpdateComponent = useCallback(
    (field: string, value: string | BulletListItem[] | undefined) => {
      const fieldPathValue = fieldPath
        ? `props.${componentPath}.${fieldPath}.${field}` // If fieldPath is provided, update the nested field
        : `props.${componentPath}.${field}`; // If fieldPath is not provided, update the top-level field
      set(currentComponent, fieldPathValue, value);
      dispatch(updateComponent(currentComponent));
    },
    [currentComponent, componentPath, fieldPath, dispatch]
  );

  const handleAddItem = () => {
    const newItem = {
      id: `${items.length + 1}`,
      value: '',
    };
    const newItems = [...items, newItem];
    setItems(newItems);
    handleUpdateComponent('items', newItems);
  };

  const handleDeleteItem = (index: number) => {
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    setItems(updatedItems);
    handleUpdateComponent('items', updatedItems);
  };

  const handleElementTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setElementType(e.target.value as ElementType);
    handleUpdateComponent('elementType', e.target.value);
    if (e.target.value === 'prosAndCons') {
      setListStyleType('none'); // Reset list style type for pros and cons
      handleUpdateComponent('listStyleType', 'none');
    }
  };

  const handleListStyleTypeChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setListStyleType(e.target.value as BulletListStyle);
    handleUpdateComponent('listStyleType', e.target.value);
  };

  const handleImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageUrl(e.target.value);
    handleUpdateComponent('imageUrl', e.target.value);
  };
  const handleImageHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageHeight(Number(e.target.value));
    handleUpdateComponent('imageHeight', e.target.value);
  };
  const handleImageWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageWidth(Number(e.target.value));
    handleUpdateComponent('imageWidth', e.target.value);
  };

  const handleListGapChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setListGap(Number(e.target.value));
    handleUpdateComponent('listGap', e.target.value);
  };

  const handleRateAnnotationChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRateAnnotation(e.target.value);
    handleUpdateComponent('rateAnnotation', e.target.value);
  };

  const handleFontSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      setFontProperties({
        ...fontProperties,
        fontSize: Number(e.target.value),
      });
      handleUpdateComponent('fontProperties.fontSize', e.target.value);
    } else {
      setFontProperties({
        ...fontProperties,
        fontSize: undefined,
      });
      handleUpdateComponent('fontProperties.fontSize', undefined);
    }
  };

  const handleFontSizeTabletChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.value) {
      setFontProperties({
        ...fontProperties,
        tablet: { fontSize: Number(e.target.value) },
      });
      handleUpdateComponent('fontProperties.tablet.fontSize', e.target.value);
    } else {
      setFontProperties({
        ...fontProperties,
        tablet: { fontSize: undefined },
      });
      handleUpdateComponent('fontProperties.tablet.fontSize', undefined);
    }
  };

  const handleFontSizeMobileChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.value) {
      setFontProperties({
        ...fontProperties,
        mobile: { fontSize: Number(e.target.value) },
      });
      handleUpdateComponent('fontProperties.mobile.fontSize', e.target.value);
    } else {
      setFontProperties({
        ...fontProperties,
        mobile: { fontSize: undefined },
      });
      handleUpdateComponent('fontProperties.mobile.fontSize', undefined);
    }
  };

  const marketplaceOfferOptions = useCallback(() => {
    const options = marketplaceOffers?.map((offer: any) => ({
      label: offer.name,
      value: offer.id,
    }));
    options.unshift({
      label: 'Select a Marketplace Offer',
      value: '',
    });
    return options;
  }, [marketplaceOffers]);

  const handleMarketplaceOfferChange = useCallback(
    (selectedOfferId: string) => {
      setMarketplaceOfferId(selectedOfferId);
      handleUpdateComponent('marketplaceOfferId', selectedOfferId);
      setItems([]);
      handleUpdateComponent('items', []);
      setRateAnnotation('');
      handleUpdateComponent('rateAnnotation', '');
    },
    [handleUpdateComponent]
  );

  return {
    currentComponent,
    componentPath,
    elementType,
    fontProperties,
    items,
    imageUrl,
    imageHeight,
    imageWidth,
    listStyleType,
    marketplaceOfferId,
    marketplaceOfferOptions,
    listGap,
    rateAnnotation,
    handleAddItem,
    handleDeleteItem,
    handleElementTypeChange,
    handleFontSizeChange,
    handleFontSizeTabletChange,
    handleFontSizeMobileChange,
    handleImageUrlChange,
    handleImageHeightChange,
    handleImageWidthChange,
    handleListGapChange,
    handleListStyleTypeChange,
    handleMarketplaceOfferChange,
    handleRateAnnotationChange,
    setItems,
    setElementType,
    setFontProperties,
    setImageUrl,
    setImageHeight,
    setImageWidth,
    setListGap,
    setListStyleType,
    setMarketplaceOfferId,
    setRateAnnotation,
  };
};
